import {
  type ComponentProps,
  type ComponentPropsWithoutRef,
  createContext,
  useContext,
} from "react";
import { tv } from "tailwind-variants";

import { SENDER_TYPE } from "@/constants/message";
import type { Message } from "@/types/message";
import { formatLongDate } from "@/utils";

type MessageBubbleContextType = {
  message: Message;
};

const MessageBubbleContext = createContext<MessageBubbleContextType | null>(null);

const useMessageBubbleContext = () => {
  const context = useContext(MessageBubbleContext);

  if (!context) {
    throw new Error("MessageBubble sub-components must be used within MessageBubble.Root");
  }

  return context;
};

export const messageBubbleVariants = tv({
  slots: {
    root: "flex flex-col gap-1 rounded-lg px-4 py-2",
    timestamp: "text-xs",
  },
  variants: {
    variant: {
      clinician: {
        root: "ml-auto w-fit bg-accent text-content-on-accent lg:max-w-md",
        timestamp: "text-content-on-accent/70",
      },
      user: {
        root: "w-fit bg-surface-sunken text-content lg:max-w-md",
        timestamp: "text-content-muted",
      },
      system: {
        root: "max-w-lg border border-warning/40 bg-warning-surface text-warning-content",
        timestamp: "text-warning-content/80",
      },
      event: {
        root: "text-center",
        timestamp: "text-content-subtle",
      },
    },
  },
});

const Root = ({ message, ...props }: { message: Message } & ComponentPropsWithoutRef<"div">) => {
  return (
    <MessageBubbleContext.Provider value={{ message }}>
      <div {...props} />
    </MessageBubbleContext.Provider>
  );
};

const SenderName = () => {
  const { message } = useMessageBubbleContext();

  if (message.senderType === SENDER_TYPE.admin) {
    return <p className="text-xs font-medium opacity-75">{message.sender.name}</p>;
  }

  if (message.senderType === SENDER_TYPE.user) {
    return (
      <p className="text-xs font-medium opacity-75">
        {message.sender.firstName} {message.sender.lastName}
      </p>
    );
  }

  return <p className="text-xs font-medium opacity-75">EO System</p>;
};

const Content = () => {
  const { message } = useMessageBubbleContext();

  return <p className="text-sm wrap-break-word">{message.content}</p>;
};

const Timestamp = ({ className }: Pick<ComponentProps<"p">, "className">) => {
  const { message } = useMessageBubbleContext();
  const { timestamp } = messageBubbleVariants();

  return <p className={timestamp({ className })}>{formatLongDate(message.createdAt)}</p>;
};

export const MessageBubble = { Root, SenderName, Content, Timestamp };
